import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Check,
  Clock,
  FileText,
  BarChart3,
  RefreshCw,
  Layers,
  Zap,
  Database,
  Cpu,
  Target,
  HelpCircle,
  Calendar,
  Download,
  ExternalLink,
  TrendingUp
} from 'lucide-react';
import SEOHead from '../../components/SEO/SEOHead';
import PortfolioComparisonImage from '../../components/PortfolioComparisonImage';

const PortfolioOptimizationSolution = () => {
  const features = [
    {
      icon: Target,
      title: 'High-Value Asset Prioritization',
      description: 'Identify patents with the strongest monetization and strategic potential so your team can focus resources where value is highest.'
    },
    {
      icon: BarChart3,
      title: 'Portfolio Health Scoring',
      description: 'Get an at-a-glance health score across quality, legal strength, market relevance, and remaining term to guide optimization decisions.'
    },
    {
      icon: RefreshCw,
      title: 'Continuous Portfolio Monitoring',
      description: 'Track portfolio quality over time as legal status, citations, and market signals change, with updated insights as new data arrives.'
    },
    {
      icon: Layers,
      title: 'Redundancy & Gap Detection',
      description: 'Surface overlapping assets and white-space gaps to reduce maintenance waste and improve strategic coverage.'
    },
    {
      icon: FileText,
      title: 'Actionable Optimization Reports',
      description: 'Generate decision-ready reports for legal, R&D, and leadership teams with clear keep/prune/improve recommendations.'
    },
    {
      icon: Zap,
      title: 'Bulk Portfolio Analysis',
      description: 'Evaluate thousands of patents in one pass using AI-powered scoring and benchmarking across your full portfolio.'
    }
  ];

  const steps = [
    {
      number: 1,
      title: 'Upload or Select Portfolio',
      description: 'Provide your patent list or company portfolio and define your optimization objective.',
      time: 'Takes 2 minutes'
    },
    {
      number: 2,
      title: 'AI Scoring & Benchmarking',
      description: 'Our engine evaluates each patent across quality, legal, market, and strategic factors.',
      time: 'Results in seconds',
      details: [
        'Citation impact and knowledge centrality',
        'Legal strength and enforceability',
        'Market relevance and adoption signals',
        'Remaining term and geographic coverage',
        'Portfolio overlap and gap analysis',
        'Industry-relative benchmark scores'
      ]
    },
    {
      number: 3,
      title: 'Execute Optimization Plan',
      description: 'Get a prioritized roadmap with recommended actions and expected value impact.',
      time: 'Ready for team execution',
      details: [
        'Prioritized keep / prune / improve list',
        'Top monetization candidates',
        'Maintenance cost reduction opportunities',
        'Coverage gaps for future filing',
        'Executive summary and technical appendix',
        'Exportable data package (PDF/CSV/XLSX)'
      ]
    }
  ];

  const faqs = [
    {
      question: 'What does portfolio optimization mean in this context?',
      answer: 'It means improving the strategic and financial performance of your portfolio by prioritizing high-value assets, reducing redundancy, and identifying filing or licensing opportunities.'
    },
    {
      question: 'Can we optimize by business unit or technology domain?',
      answer: 'Yes. You can segment and analyze subsets of your portfolio by assignee group, technology area, jurisdiction, or custom tags.'
    },
    {
      question: 'How often should we run optimization analysis?',
      answer: 'Most teams run a quarterly review, with monthly monitoring for high-volume portfolios or fast-moving technology sectors.'
    },
    {
      question: 'Does this replace legal strategy?',
      answer: 'No. PricePatent provides data-driven prioritization and recommendations that support legal and business teams; final decisions should include counsel review.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <SEOHead
        title="Patent Portfolio Optimization | PricePatent"
        description="Optimize your patent portfolio with AI-powered scoring, benchmarking, and action plans. Prioritize high-value assets and improve portfolio performance."
        keywords="patent portfolio optimization, IP portfolio strategy, patent prioritization, patent benchmarking, IP analytics"
        ogTitle="Patent Portfolio Optimization | PricePatent"
        ogDescription="Prioritize high-value patents, reduce redundancy, and execute a data-driven optimization roadmap."
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Patent Portfolio Optimization',
          description: 'AI-powered portfolio optimization and prioritization for intellectual property teams',
          provider: {
            '@type': 'Organization',
            name: 'PricePatent'
          },
          serviceType: 'Patent Portfolio Optimization',
          category: 'IP Analytics Solutions'
        }}
      />

      <div className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 pt-20 pb-24">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3">
            <svg width="800" height="800" viewBox="0 0 800 800" fill="none">
              <circle cx="400" cy="400" r="400" fill="url(#heroGradient)" fillOpacity="0.1" />
              <defs>
                <radialGradient id="heroGradient" cx="0" cy="0" r="1">
                  <stop stopColor="#2563EB" />
                  <stop offset="1" stopColor="#2563EB" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full text-blue-600 text-sm font-medium mb-6">
              <Layers className="h-4 w-4 mr-2" />
              Portfolio Optimization Solution
            </div>
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl mb-6 leading-tight">
              <span className="block pb-1">AI-Powered</span>
              <span className="block mt-2 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent pb-1">
                Patent Portfolio Optimization
              </span>
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600 mb-8">
              Focus on the patents that matter most. Prioritize high-value assets, reduce inefficiencies, and maximize portfolio impact.
            </p>

            <PortfolioComparisonImage />

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Schedule Demo
                <Calendar className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/sample-report"
                className="inline-flex items-center justify-center px-8 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-all duration-300 shadow-sm hover:shadow-md"
              >
                See Sample Analysis
                <ExternalLink className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Optimize portfolio performance with clear, data-backed priorities
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                  <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              From portfolio review to action plan in 3 steps
            </h2>
          </div>
          <div className="space-y-12">
            {steps.map((step) => (
              <div key={step.number} className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-semibold text-gray-900">
                    {step.number}. {step.title}
                  </h3>
                  <span className="text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                    {step.time}
                  </span>
                </div>
                <p className="text-gray-600 text-lg leading-relaxed mb-4">{step.description}</p>
                {step.details && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {step.details.map((detail, idx) => (
                      <div key={idx} className="flex items-center bg-blue-50 rounded-lg p-3">
                        <Check className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{detail}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-purple-50 rounded-lg flex items-center justify-center mx-auto mb-4">
              <HelpCircle className="h-8 w-8 text-purple-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 border border-gray-100 hover:shadow-md transition-shadow duration-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl mb-4">
              Turn portfolio complexity into strategic clarity
            </h2>
            <p className="mt-4 text-xl text-blue-100 mb-8">
              See which patents to prioritize, protect, monetize, or prune.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-blue-600 bg-white hover:bg-blue-50 transition-colors duration-300 shadow-lg hover:shadow-xl"
              >
                Schedule a Demo
                <Calendar className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/demo"
                className="inline-flex items-center justify-center px-8 py-3 border border-white/30 text-base font-medium rounded-lg text-white bg-blue-700 hover:bg-blue-800 transition-colors duration-300"
              >
                Try Free Analysis
                <TrendingUp className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/sample-report"
                className="inline-flex items-center justify-center px-8 py-3 border border-white/30 text-base font-medium rounded-lg text-white hover:bg-white/10 transition-colors duration-300"
              >
                View Sample Report
                <Download className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortfolioOptimizationSolution;

